import { IBackgroundCheckSettingsResponse } from "@wingspanhq/background-checks/dist/lib/interfaces";
import { toWSDate, WSGrid, WSText } from "@wingspanhq/fe-component-library";
import {
  IRequirementDefinitionResponse,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import { ISharedFileRequestSettingsResponse } from "@wingspanhq/shared-files/dist/lib/interfaces";
import { ISignedDocumentTemplateResponse } from "@wingspanhq/signed-documents/dist/lib/interfaces";
import type { insuranceMonitorSettingsApiSpec } from "@wingspanhq/wingspan-api-types";
import { useMemo } from "react";

import {
  FieldView,
  FieldViewProps
} from "../../../../shared/components/FieldView/FieldView";
import { RequirementInstructionsView } from "../../../Requirements/components/RequirementInstructionsView/RequirementInstructionsView";
import { IDENTITY_VERIFICATION_REQUIREMENT_INSTRUCTIONS } from "../../../Requirements/modules/IdentityVerification/instructions";
import { selectorIsAcceptedSourceTypeIncluded } from "../../../Requirements/modules/InsuranceCoverage/selectors/selectorIsAcceptedSourceTypeIncluded";
import { selectorPolicyTypesFromSettings } from "../../../Requirements/modules/InsuranceCoverage/selectors/selectorPolicyTypesFromSettings";
import { mimeTypesToLabel } from "../../modules/SharedFile/createRequirementDefinition/utils";
import { InsuranceInstructionsView } from "../../routes/RouteCreateRequirementByTemplate/components/InsuranceInstructionsView";
import { getRequirementDefinitionTemplateByParams } from "../../utils/getRequirementDefinitionTemplateByParams";

type Props = {
  requirementDefinition: IRequirementDefinitionResponse;
  singedDocumentTemplate?: ISignedDocumentTemplateResponse;
  sharedFileSettings?: ISharedFileRequestSettingsResponse;
  backgroundChecksSettings?: IBackgroundCheckSettingsResponse;
  monitorSettings?: insuranceMonitorSettingsApiSpec.InsuranceCoverageMonitorSettings;
};

export const RequirementInformation: React.FC<Props> = ({
  requirementDefinition,
  singedDocumentTemplate,
  sharedFileSettings,
  backgroundChecksSettings,
  monitorSettings
}) => {
  const fields = useMemo(() => {
    const fieldsByType: Record<RequirementType, FieldViewProps[]> = {
      [RequirementType.Signature]: [
        {
          label: "Required signatures",
          value:
            singedDocumentTemplate?.roles?.length === 2
              ? "Both contractors and client signatures required"
              : "Contractor signature required"
        }
      ],
      [RequirementType.SharedFile]: [
        {
          label: "File types",
          value: mimeTypesToLabel(sharedFileSettings?.mimeTypes || [])
        },
        {
          fullWidth: true,
          label: "Instructions",
          value: sharedFileSettings?.instructions ? (
            <RequirementInstructionsView
              instructions={sharedFileSettings.instructions}
            />
          ) : undefined
        }
      ],
      [RequirementType.BackgroundCheck]: [
        {
          label: "Package",
          value: backgroundChecksSettings?.package?.title
        },
        {
          label: "Name",
          value: backgroundChecksSettings?.title
        },
        {
          fullWidth: true,
          label: "Instructions",
          value: backgroundChecksSettings?.instructions ? (
            <RequirementInstructionsView
              instructions={backgroundChecksSettings.instructions}
            />
          ) : undefined
        }
      ],
      [RequirementType.IdentityVerification]: [
        {
          fullWidth: true,
          label: "Instructions",
          value: (
            <RequirementInstructionsView
              instructions={IDENTITY_VERIFICATION_REQUIREMENT_INSTRUCTIONS}
            />
          )
        }
      ],
      [RequirementType.InsuranceCoverage]: [
        {
          fullWidth: true,
          label: "Instructions",
          value: (
            <InsuranceInstructionsView
              allowMasterPolicy={selectorIsAcceptedSourceTypeIncluded(
                "PlanEnrollment",
                monitorSettings
              )}
              insuranceName={
                getRequirementDefinitionTemplateByParams({
                  policyType: selectorPolicyTypesFromSettings(monitorSettings),
                  type: RequirementType.InsuranceCoverage
                }).name
              }
              policyTypes={selectorPolicyTypesFromSettings(monitorSettings)}
            />
          )
        }
      ],
      [RequirementType.Registration]: [],
      [RequirementType.TaxVerification]: [],
      [RequirementType.PayoutMethod]: []
    };

    const result: FieldViewProps[] = [
      {
        label: "Creation date",
        value: toWSDate(requirementDefinition.createdAt).formattedValue
      },
      {
        label: "Expiration",
        value: requirementDefinition.expirationDays
          ? `Expire in ${requirementDefinition.expirationDays} days`
          : "Never expires"
      },
      {
        fullWidth: true,
        label: "Description",
        value: (
          <WSText.ParagraphSm as="pre">
            {requirementDefinition.description}
          </WSText.ParagraphSm>
        )
      },
      {
        label: "Manual renewal allowed",
        value: requirementDefinition.manualRenewalAllowed ? "Yes" : "No"
      },
      ...fieldsByType[requirementDefinition.requirementType]
    ].filter(i => i.value);

    return result;
  }, [
    singedDocumentTemplate,
    sharedFileSettings,
    requirementDefinition,
    backgroundChecksSettings
  ]);

  return (
    <WSGrid gutter="2XL">
      {fields.map(({ label, value, fullWidth }) => (
        <WSGrid.Item
          key={label}
          span={fullWidth ? { xs: "12" } : { l: "6", s: "6", xs: "12" }}
        >
          <FieldView label={label} value={value} />
        </WSGrid.Item>
      ))}
    </WSGrid>
  );
};
